import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Tag, Button, Space, message, Select, Card, Tabs, Form, Input, Modal, Popconfirm, Collapse, Checkbox, Divider, Pagination, Dropdown, Menu } from 'antd';
import { CheckOutlined, CloseOutlined, UserAddOutlined, DeleteOutlined, EditOutlined, DownOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { getCurrentUser, setCurrentUser, removeToken } from '../utils/auth';
import { getDataForms, formatFieldValue as formatFieldValueShared, getFieldsForDataType as getFieldsForDataTypeShared, getDataTypeLabel as getDataTypeLabelShared, PHD_TYPES } from '../utils/dataForms';
import { formatDateTime } from '../utils/timeUtils';
import { groupSubmissionsByBatch } from '../utils/submissionUtils';
import DataTable from './DataTable';
import PhdReviewMatrix from './PhdReviewMatrix';
import RejectModal from './shared/RejectModal';
import BatchReviewList from './shared/BatchReviewList';

const { TabPane } = Tabs;

const AdminPanel = ({ onUserUpdate, onNavigateToDepartment }) => {
  const [currentUser] = useState(getCurrentUser());
  // Only super admins (not affiliated with any department); departments have regular users
  const isSuperAdmin = currentUser?.role === 'admin' && !currentUser?.department_id;
  
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(isSuperAdmin ? 'department-stats' : 'pending');
  const [deptStatusFilter, setDeptStatusFilter] = useState('all'); // Status filter for department review view
  const [deptBatchPage, setDeptBatchPage] = useState(1); // Batch pagination for department review view
  const [deptBatchPageSize, setDeptBatchPageSize] = useState(10); // Batch page size for department review view
  const [batchPage, setBatchPage] = useState(1); // Batch pagination for standard review view
  const [batchPageSize, setBatchPageSize] = useState(10); // Batch page size for standard review view
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userForm] = Form.useForm();
  const [departmentStats, setDepartmentStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedReviewKeys, setSelectedReviewKeys] = useState([]); // Selected pending review record IDs (by submission ID)
  const [reviewing, setReviewing] = useState(false); // Whether currently reviewing
  // Rejection questionnaire state
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectTargetIds, setRejectTargetIds] = useState([]);
  const [rejectTargetSubmissions, setRejectTargetSubmissions] = useState([]); // Save data rows to reject for display
  const [rejectForm] = Form.useForm();

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    // Clear selection when switching status
    setSelectedReviewKeys([]);
    // Reset pagination when switching status
    setBatchPage(1);
    
    if (isSuperAdmin) {
      if (statusFilter === 'department-stats') {
        loadDepartmentStats();
      } else if (statusFilter.startsWith('dept-')) {
        // View detailed review status for a specific department
        const deptId = parseInt(statusFilter.replace('dept-', ''));
        loadSubmissionsForDepartment(deptId);
      }
    } else {
      if (statusFilter !== 'users') {
        loadSubmissions();
      }
    }
  }, [statusFilter, isSuperAdmin]);
  
  // When the department review status filter changes, no need to reload data; frontend filtering is sufficient

  useEffect(() => {
    if (departments.length > 0 && isSuperAdmin && statusFilter === 'department-stats') {
      loadDepartmentStats();
    }
  }, [departments, isSuperAdmin, statusFilter]);

  useEffect(() => {
    if (statusFilter === 'users') {
      loadUsers();
    }
  }, [statusFilter]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const response = await api.get('/submissions', { params });
      // Filter out data items containing _replaced_by field (overwritten data)
      const filteredData = (response.data || []).filter(sub => {
        if (!sub.data_content) return true;
        const content = typeof sub.data_content === 'string' 
          ? JSON.parse(sub.data_content) 
          : sub.data_content;
        return !content || !content._replaced_by;
      });
      // Ensure descending sort by submission time (newest first)
      filteredData.sort((a, b) => {
        const timeA = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const timeB = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return timeB - timeA;
      });
      console.log(`AdminPanel: 加载了 ${filteredData.length} 条记录，状态筛选: ${statusFilter}`);
      if (statusFilter === 'pending') {
        const pendingCount = filteredData.filter(s => s.status === 'pending').length;
        console.log(`AdminPanel: 其中 ${pendingCount} 条是待审核状态`);
      }
      setSubmissions(filteredData);
    } catch (error) {
      console.error('加载提交记录失败:', error);
      message.error('加载提交记录失败');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentStats = async () => {
    setStatsLoading(true);
    try {
      // Load all submissions, grouped by department
      const response = await api.get('/submissions');
      const allSubmissions = response.data || [];
      
      // Aggregate review stats by department
      const statsMap = {};
      allSubmissions.forEach(sub => {
        const deptId = sub.department_id;
        if (!statsMap[deptId]) {
          statsMap[deptId] = {
            department_id: deptId,
            pending: 0,
            approved: 0,
            rejected: 0,
            total: 0
          };
        }
        statsMap[deptId][sub.status] = (statsMap[deptId][sub.status] || 0) + 1;
        statsMap[deptId].total += 1;
      });
      
      // Convert to array and add department names
      const stats = Object.values(statsMap).map(stat => {
        const dept = departments.find(d => d.id === stat.department_id);
        return {
          ...stat,
          department_name: dept ? dept.name : `部门ID: ${stat.department_id}`
        };
      }).sort((a, b) => a.department_name.localeCompare(b.department_name));
      
      setDepartmentStats(stats);
    } catch (error) {
      message.error('加载部门审核情况失败');
    } finally {
      setStatsLoading(false);
    }
  };

  const loadSubmissionsForDepartment = async (deptId) => {
    setLoading(true);
    try {
      const response = await api.get('/submissions', { params: { department_id: deptId } });
      // Filter out data items containing _replaced_by field (overwritten data)
      const filteredData = (response.data || []).filter(sub => {
        if (!sub.data_content) return true;
        const content = typeof sub.data_content === 'string' 
          ? JSON.parse(sub.data_content) 
          : sub.data_content;
        return !content || !content._replaced_by;
      });
      setSubmissions(filteredData);
    } catch (error) {
      message.error('加载部门提交记录失败');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data || []);
    } catch (error) {
      message.error('加载部门列表失败');
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/users');
      console.log('Users loaded:', response.data);
      // Check the returned data structure
      if (response.data && response.data.length > 0) {
        const firstUser = response.data[0];
        console.log('First user keys:', Object.keys(firstUser));
        console.log('First user data:', firstUser);
        console.log('First user is_online:', firstUser.is_online, 'type:', typeof firstUser.is_online);
        console.log('First user last_login_at:', firstUser.last_login_at, 'type:', typeof firstUser.last_login_at);
      }
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      message.error('加载用户列表失败');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleReview = async (id, status, silent = false, extraData = {}) => {
    try {
      await api.post(`/submissions/${id}/review`, { status, ...extraData });
      if (!silent) {
        message.success(status === 'approved' ? '已通过审核' : '已拒绝');
      }
      loadSubmissions();
    } catch (error) {
      if (!silent) {
        message.error(error.response?.data?.error || '审核失败');
      }
      throw error; // Re-throw the error for the caller to handle
    }
  };

  // Open the rejection questionnaire (single or batch)
  const openRejectModal = (ids) => {
    if (!ids || ids.length === 0) {
      message.warning('请选择要拒绝的记录');
      return;
    }
    setRejectTargetIds(ids);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  // Submit the rejection questionnaire
  const handleRejectSubmit = async () => {
    try {
      const values = await rejectForm.validateFields();
      let { reason, detail } = values;
      // If multi-select reasons, combine into a string
      if (Array.isArray(reason)) {
        reason = reason.join('；');
      }
      const extraData = { reason, detail };

      setRejectSubmitting(true);
      // Decide whether to use single or batch API based on count
      if (rejectTargetIds.length === 1) {
        await handleReview(rejectTargetIds[0], 'rejected', true, extraData);
      } else {
        const response = await api.post('/submissions/batch-review', {
          submission_ids: rejectTargetIds,
          status: 'rejected',
          ...extraData
        });
        if (response.data && response.data.updated_count > 0) {
          message.success(`成功拒绝 ${response.data.updated_count} 条记录`);
        } else {
          message.warning('批量拒绝完成，但未更新任何记录');
        }
      }

      // Refresh data
      if (statusFilter.startsWith('dept-')) {
        const deptId = parseInt(statusFilter.replace('dept-', ''));
        loadSubmissionsForDepartment(deptId);
      } else {
        loadSubmissions();
      }

      // Clear selection
      setSelectedReviewKeys([]);
      setRejectModalVisible(false);
      setRejectTargetIds([]);
      setRejectTargetSubmissions([]);
    } catch (error) {
      if (error && error.errorFields) {
        // Form validation error, do not prompt
        return;
      }
      console.error('提交拒绝问卷失败:', error);
      message.error(error.response?.data?.error || '提交拒绝信息失败');
    } finally {
      setRejectSubmitting(false);
    }
  };

  // No longer review by "entire batch"; unified to row-based selection (handleApproveSelected / handleRejectSelected)

  // Batch approve using currently checked records
  const handleApproveSelected = async () => {
    if (!selectedReviewKeys || selectedReviewKeys.length === 0) {
      message.warning('请先勾选要通过的记录');
      return;
    }

    // Get pending records based on current page
    let pendingSelected = [];
    if (statusFilter.startsWith('dept-')) {
      // Department review page: filter from current department's submissions
      const deptId = parseInt(statusFilter.replace('dept-', ''));
      const deptSubmissions = submissions.filter(s => s.department_id === deptId);
      pendingSelected = deptSubmissions.filter(
        (s) => s.status === 'pending' && selectedReviewKeys.includes(s.id)
      );
    } else {
      // Standard review page: filter from all submissions
      pendingSelected = submissions.filter(
        (s) => s.status === 'pending' && selectedReviewKeys.includes(s.id)
      );
    }

    if (pendingSelected.length === 0) {
      message.warning('选中的记录中没有待审核的数据');
      return;
    }

    setReviewing(true);
    try {
      const response = await api.post('/submissions/batch-review', {
        submission_ids: pendingSelected.map((s) => s.id),
        status: 'approved'
      });

      if (response.data && response.data.updated_count > 0) {
        message.success(`已通过 ${response.data.updated_count} 条选中记录`);
      } else {
        message.warning('批量通过完成，但未更新任何记录');
      }

      // Refresh data
      if (statusFilter.startsWith('dept-')) {
        const deptId = parseInt(statusFilter.replace('dept-', ''));
        loadSubmissionsForDepartment(deptId);
      } else {
        loadSubmissions();
      }
      // Clear selection after operation completes
      setSelectedReviewKeys([]);
    } catch (error) {
      console.error('批量通过选中记录失败:', error);
      message.error(error.response?.data?.error || '批量通过选中记录失败');
    } finally {
      setReviewing(false);
    }
  };

  // Batch reject using currently checked records (opens rejection questionnaire)
  const handleRejectSelected = () => {
    if (!selectedReviewKeys || selectedReviewKeys.length === 0) {
      message.warning('请先勾选要拒绝的记录');
      return;
    }

    // Get pending records based on current page
    let pendingSelected = [];
    if (statusFilter.startsWith('dept-')) {
      // Department review page: filter from current department's submissions
      const deptId = parseInt(statusFilter.replace('dept-', ''));
      const deptSubmissions = submissions.filter(s => s.department_id === deptId);
      pendingSelected = deptSubmissions.filter(
        (s) => s.status === 'pending' && selectedReviewKeys.includes(s.id)
      );
    } else {
      // Standard review page: filter from all submissions
      pendingSelected = submissions.filter(
        (s) => s.status === 'pending' && selectedReviewKeys.includes(s.id)
      );
    }

    if (pendingSelected.length === 0) {
      message.warning('选中的记录中没有待审核的数据');
      return;
    }

    const pendingIds = pendingSelected.map((s) => s.id);
    // Save selected data rows for display in the modal
    setRejectTargetSubmissions(pendingSelected);
    openRejectModal(pendingIds);
  };

  // Get pending review records (optimized with useMemo)
  // Pending review records
  const pendingSubmissions = useMemo(() => {
    return submissions.filter(s => s.status === 'pending');
  }, [submissions]);

  // Group submissions by batch (applicable to all statuses)
  // Group pending records by batch (optimized with useMemo to avoid recalculation on every render)
  const pendingBatches = useMemo(() => {
    if (pendingSubmissions.length === 0) return [];
    return groupSubmissionsByBatch(pendingSubmissions);
  }, [pendingSubmissions]);

  // Group approved records by batch (optimized with useMemo)
  // Approved records
  const approvedBatches = useMemo(() => {
    const filtered = submissions.filter(s => s.status === 'approved');
    if (filtered.length === 0) return [];
    return groupSubmissionsByBatch(filtered);
  }, [submissions]);

  // Group rejected records by batch (optimized with useMemo)
  const rejectedBatches = useMemo(() => {
    const filtered = submissions.filter(s => s.status === 'rejected');
    if (filtered.length === 0) return [];
    return groupSubmissionsByBatch(filtered);
  }, [submissions]);

  // Group all records by batch (optimized with useMemo)
  const allBatches = useMemo(() => {
    if (submissions.length === 0) return [];
    const filtered = submissions;
    if (filtered.length === 0) return [];
    return groupSubmissionsByBatch(filtered);
  }, [submissions]);

  const handleCreateUser = async (values) => {
    try {
      const userData = {
        username: values.username,
        password: values.password,
        role: values.role || 'user', // Can only create regular users
        department_id: values.department_id // Department must be specified
      };
      
      await api.post('/register', userData);
      message.success('用户创建成功');
      setUserModalVisible(false);
      setEditingUserId(null);
      userForm.resetFields();
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || '创建用户失败');
    }
  };

  const handleEditUser = (user) => {
    setEditingUserId(user.id);
    // Get the currently logged-in user (avoid using potentially uninitialized state)
    const loggedInUser = getCurrentUser();
    // When super admin edits themselves, only set the password field
    if (isSuperAdmin && user.id === loggedInUser?.id) {
      userForm.setFieldsValue({
        password: undefined // Leave password empty, requires entering a new password
      });
    } else {
      userForm.setFieldsValue({
        username: user.username,
        role: user.role,
        department_id: user.department_id,
        password: undefined // Leave password empty, optional to change
      });
    }
    setUserModalVisible(true);
  };

  const handleUpdateUser = async (values) => {
    try {
      // Verify editingUserId exists
      if (!editingUserId) {
        console.error('handleUpdateUser: editingUserId is null or undefined');
        message.error('用户ID不存在，无法更新');
        return;
      }
      
      console.log('handleUpdateUser: editingUserId =', editingUserId, 'values =', { ...values, password: values.password ? '***' : '' });
      
      // Get the currently logged-in user (avoid using potentially uninitialized state)
      const loggedInUser = getCurrentUser();

      const updateData = {};

      // When super admin edits themselves, only the password can be modified
      if (isSuperAdmin && editingUserId === loggedInUser?.id) {
        if (values.password && values.password.trim() !== '') {
          // Only send password field, do not send other fields (even if present in the form)
          updateData.password = values.password;
          // Ensure no other fields are included to avoid backend validation failure
          // Backend checks: if username, role, or department_id is present, it will return an error
        } else {
          message.warning('请输入新密码');
          return;
        }
      } else {
        // When editing other users, other fields can be modified
        if (values.username) {
          updateData.username = values.username;
        }
        
        // Super admin can modify role (but cannot create department admins, only regular users)
        if (isSuperAdmin && values.role && values.role === 'user') {
          updateData.role = values.role;
        }
        
        // If a new password is provided, update it
        if (values.password && values.password.trim() !== '') {
          updateData.password = values.password;
        }
        
        // Super admin can modify the department
        if (isSuperAdmin && values.department_id) {
          updateData.department_id = values.department_id;
        }
      }
      
      // Verify if there is data to update
      if (Object.keys(updateData).length === 0) {
        console.warn('handleUpdateUser: updateData is empty');
        message.warning('没有需要更新的数据');
        return;
      }
      
      console.log('handleUpdateUser: Sending API request to /users/' + editingUserId, { ...updateData, password: updateData.password ? '***' : '' });
      const response = await api.put(`/users/${editingUserId}`, updateData);
      console.log('handleUpdateUser: API response received', response.data);
      message.success('用户更新成功');
      
      // If the logged-in user's password was changed, force re-login
      const currentUser = getCurrentUser();
      if (currentUser && currentUser.id === editingUserId && updateData.password) {
        // Clear token and user info
        removeToken();
        message.info('密码已更新，请重新登录');
        // Delay navigation to login page so the user sees the notification
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return; // Return early, do not execute subsequent operations
      }
      
      // If modifying the logged-in user's other info (non-password), update localStorage
      if (currentUser && currentUser.id === editingUserId && response.data?.user) {
        const updatedUser = response.data.user;
        setCurrentUser(updatedUser);
        // Notify parent component to update user state
        if (onUserUpdate) {
          onUserUpdate(updatedUser);
        }
        message.info('您的用户信息已更新，页面将刷新以应用更改');
        // Delay page refresh so the user sees the notification
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      
      setUserModalVisible(false);
      setEditingUserId(null);
      userForm.resetFields();
      loadUsers();
    } catch (error) {
      console.error('handleUpdateUser: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      });
      const errorMessage = error.response?.data?.error || error.message || '更新用户失败';
      message.error(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      message.success('用户删除成功');
      loadUsers();
    } catch (error) {
      message.error(error.response?.data?.error || '删除用户失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { color: 'orange', text: '待审核' },
      approved: { color: 'green', text: '已通过' },
      rejected: { color: 'red', text: '已拒绝' }
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  // Get field definitions by data type (delegates to shared util, preserves departmentId parameter signature)
  const getFieldsForDataType = (dataType, departmentId) => {
    if (!departmentId) return [];
    const dept = departments.find(d => d.id === departmentId);
    if (!dept) return [];
    return getFieldsForDataTypeShared(getDataForms(dept.name), dataType);
  };

  // Get Chinese label by data type (delegates to shared util, preserves departmentId parameter signature)
  const getDataTypeLabel = (dataType, departmentId) => {
    if (!departmentId) return dataType;
    const dept = departments.find(d => d.id === departmentId);
    if (!dept) return dataType;
    return getDataTypeLabelShared(getDataForms(dept.name), dataType);
  };

  // Format field value display (delegates to shared util)
  const formatFieldValue = (field, value) => formatFieldValueShared(field, value);

  // Generic sort function (supports number, date, string)
  const getSorter = (key, fieldType) => {
    return (a, b) => {
      let aVal = a.data_content?.[key];
      let bVal = b.data_content?.[key];
      
      // Handle null values
      if (aVal === null || aVal === undefined || aVal === '') aVal = null;
      if (bVal === null || bVal === undefined || bVal === '') bVal = null;
      
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      
      // Sort by field type
      if (fieldType === 'number' || fieldType === 'count') {
        const aNum = parseFloat(aVal) || 0;
        const bNum = parseFloat(bVal) || 0;
        return aNum - bNum;
      } else if (fieldType === 'date') {
        const aDate = new Date(aVal);
        const bDate = new Date(bVal);
        return aDate - bDate;
      } else {
        // String sort
        return String(aVal).localeCompare(String(bVal), 'zh-CN');
      }
    };
  };

  // Build dynamic columns (expand based on data content fields, aligned with regular user history)
  const buildColumns = (submissionsData) => {
    // Batch data does not need to show submission time and submitter
    const baseColumns = [];

    // If submissionsData has data, get field definitions from the first record's data_type and department_id
    if (submissionsData && submissionsData.length > 0) {
      const firstRecord = submissionsData[0];
      const fields = getFieldsForDataType(firstRecord.data_type, firstRecord.department_id);
      const dataContent = firstRecord.data_content || {};
      
      // Create columns in the order of field definitions (consistent with data entry)
      fields.forEach(field => {
        if (!field.computed) {
          // Create column even if field is not in dataContent (display as '-')
          baseColumns.push({
            title: field.label,
            key: field.key,
            width: 150,
            render: (_, record) => {
              const value = record.data_content?.[field.key];
              return formatFieldValue(field, value) ?? '-';
            },
            sorter: getSorter(field.key, field.type)
          });
        }
      });
      
      // Also display fields in dataContent that are not in the field definitions (compatibility, appended at the end)
      // Exclude system fields: isDraft, isNew, _replaced_by, etc.
      const systemFields = new Set(['isDraft', 'isNew', '_replaced_by', 'key', 'id', 'status']);
      const definedKeys = new Set(fields.map(f => f.key));
      Object.keys(dataContent).forEach(key => {
        if (!definedKeys.has(key) && !systemFields.has(key)) {
          // Attempt to infer the field type
          const sampleValue = dataContent[key];
          let fieldType = 'string';
          if (typeof sampleValue === 'number') {
            fieldType = 'number';
          } else if (sampleValue && typeof sampleValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(sampleValue)) {
            fieldType = 'date';
          }
          
          baseColumns.push({
            title: key,
            key: key,
            width: 150,
            render: (_, record) => {
              const value = record.data_content?.[key];
              return value !== undefined && value !== null ? String(value) : '-';
            },
            sorter: getSorter(key, fieldType)
          });
        }
      });
    }

    return baseColumns;
  };

  // Default columns (used when there is no data)
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: '部门ID',
      dataIndex: 'department_id',
      key: 'department_id',
      width: 100
    },
    {
      title: '数据类型',
      dataIndex: 'data_type',
      key: 'data_type',
      width: 200
    },
    {
      title: '年份',
      dataIndex: 'year',
      key: 'year',
      width: 100
    },
  ];

  const userColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      sorter: (a, b) => (a.username || '').localeCompare(b.username || '', 'zh-CN')
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      sorter: (a, b) => (a.role || '').localeCompare(b.role || '', 'zh-CN'),
      render: (role, record) => {
        if (role === 'admin') {
          // Only super admin (no department_id)
          return <Tag color="red">系统管理员</Tag>;
        }
        return <Tag color="blue">普通用户</Tag>;
      }
    },
    {
      title: '所属部门',
      dataIndex: 'department_id',
      key: 'department_id',
      width: 200,
      sorter: (a, b) => {
        const aDept = departments.find(d => d.id === a.department_id);
        const bDept = departments.find(d => d.id === b.department_id);
        const aName = aDept ? aDept.name : '';
        const bName = bDept ? bDept.name : '';
        return aName.localeCompare(bName, 'zh-CN');
      },
      render: (deptId, record) => {
        // System admin has no department
        if (record.role === 'admin' && !deptId) {
          return <Tag color="default">无</Tag>;
        }
        // Regular user: display the department
        if (!deptId) return '-';
        const dept = departments.find(d => d.id === deptId);
        return dept ? dept.name : `部门ID: ${deptId}`;
      }
    },
    {
      title: '在线状态',
      dataIndex: 'is_online',
      key: 'is_online',
      width: 120,
      sorter: (a, b) => {
        const aOnline = a.is_online === true || a.is_online === 'true' || a.is_online === 1;
        const bOnline = b.is_online === true || b.is_online === 'true' || b.is_online === 1;
        return aOnline === bOnline ? 0 : aOnline ? 1 : -1;
      },
      render: (isOnline, record) => {
        // Handle boolean or string values to ensure correct display
        const online = isOnline === true || isOnline === 'true' || isOnline === 1;
        console.log('User online status:', record.username, 'isOnline:', isOnline, 'online:', online);
        if (online) {
          return <Tag color="green">在线</Tag>;
        } else {
          return <Tag color="default">离线</Tag>;
        }
      }
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      width: 180,
      sorter: (a, b) => {
        const aTime = a.last_login_at ? new Date(a.last_login_at) : new Date(0);
        const bTime = b.last_login_at ? new Date(b.last_login_at) : new Date(0);
        return aTime - bTime;
      },
      render: (lastLoginAt, record) => {
        return formatDateTime(lastLoginAt);
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => {
        // Permission check
        // Only super admin can edit and delete users
        const canEdit = isSuperAdmin;
        const canDelete = isSuperAdmin;
        
        if (!canEdit && !canDelete) {
          return <span style={{ color: '#999' }}>无权限</span>;
        }
        
        return (
          <Space>
            {canEdit && (
              <Button 
                type="primary" 
                size="small" 
                icon={<EditOutlined />}
                onClick={() => handleEditUser(record)}
              >
                编辑
              </Button>
            )}
            {canDelete && (
              <Popconfirm
                title="确定要删除这个用户吗？"
                onConfirm={() => handleDeleteUser(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button danger size="small" icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  // Department review stats table columns
  const departmentStatsColumns = [
    {
      title: '部门名称',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 200,
      sorter: (a, b) => (a.department_name || '').localeCompare(b.department_name || '', 'zh-CN')
    },
    {
      title: '待审核',
      dataIndex: 'pending',
      key: 'pending',
      width: 100,
      sorter: (a, b) => (a.pending || 0) - (b.pending || 0),
      render: (count) => <Tag color="orange">{count}</Tag>
    },
    {
      title: '已通过',
      dataIndex: 'approved',
      key: 'approved',
      width: 100,
      sorter: (a, b) => (a.approved || 0) - (b.approved || 0),
      render: (count) => <Tag color="green">{count}</Tag>
    },
    {
      title: '已拒绝',
      dataIndex: 'rejected',
      key: 'rejected',
      width: 100,
      sorter: (a, b) => (a.rejected || 0) - (b.rejected || 0),
      render: (count) => <Tag color="red">{count}</Tag>
    },
    {
      title: '总计',
      dataIndex: 'total',
      key: 'total',
      width: 100
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => {
        // Find sub-departments of the lead department (parent_id not in departments list)
        const departmentIds = new Set(departments.map(d => d.id));
        const leadDeptChildren = departments.filter(dept => 
          dept.parent_id && !departmentIds.has(dept.parent_id)
        );
        const leadDeptId = leadDeptChildren.length > 0 ? leadDeptChildren[0].parent_id : null;
        
        // Check if this department belongs to the lead department (its parent_id equals leadDeptId)
        const isLeadDeptChild = record.department_id && leadDeptId && 
          departments.find(d => d.id === record.department_id)?.parent_id === leadDeptId;
        
        // If the department belongs to the lead department, navigate to lead department's DepartmentView data review page
        // Otherwise, navigate to the department review details page
        return (
          <Button
            type="link"
            onClick={() => {
              if (isLeadDeptChild && onNavigateToDepartment) {
                // Navigate to lead department's DepartmentView and open the data review tab
                // Need to find the first child department of the lead department as the target
                const targetDept = leadDeptChildren.find(d => d.id === record.department_id) || leadDeptChildren[0];
                if (targetDept) {
                  onNavigateToDepartment(targetDept.id, 'review');
                }
              } else {
                // Switch to the department's data review page (pending status)
                setStatusFilter(`dept-${record.department_id}`);
                setDeptStatusFilter('pending'); // Default to pending display
              }
            }}
          >
            查看详情
          </Button>
        );
      }
    }
  ];

  return (
    <Card>
      <h2>管理员面板</h2>
      <Tabs
        activeKey={statusFilter.startsWith('dept-') ? 'department-stats' : statusFilter}
        onChange={(key) => {
          setStatusFilter(key);
        }}
        style={{ marginBottom: '20px' }}
      >
        {isSuperAdmin ? (
          <>
            <TabPane tab="部门审核情况" key="department-stats" />
            <TabPane tab="用户管理" key="users" />
          </>
        ) : (
          <>
            <TabPane tab="数据审核" key="pending" />
        <TabPane tab="已通过" key="approved" />
        <TabPane tab="已拒绝" key="rejected" />
        <TabPane tab="全部" key="all" />
            <TabPane tab="用户管理" key="users" />
          </>
        )}
      </Tabs>
      {statusFilter === 'department-stats' ? (
        <Table
          columns={departmentStatsColumns}
          dataSource={departmentStats}
          rowKey="department_id"
          loading={statsLoading}
          pagination={{ pageSize: 10 }}
          size="middle"
          bordered
        />
      ) : statusFilter.startsWith('dept-') ? (
        <>
          <div style={{ marginBottom: '16px' }}>
            <Button onClick={() => setStatusFilter('department-stats')}>
              返回部门列表
            </Button>
            <span style={{ marginLeft: '16px' }}>
              {(() => {
                const deptId = parseInt(statusFilter.replace('dept-', ''));
                const dept = departments.find(d => d.id === deptId);
                return dept ? `部门：${dept.name}` : '';
              })()}
            </span>
          </div>
          
          {/* 部门审核界面的状态筛选标签 */}
          <Tabs
            activeKey={deptStatusFilter}
            onChange={(key) => {
              setDeptStatusFilter(key);
              setSelectedReviewKeys([]); // Clear selection when switching status
              setDeptBatchPage(1); // Reset to first page when switching status
            }}
            style={{ marginBottom: '16px' }}
          >
            <TabPane tab="待审核" key="pending" />
            <TabPane tab="已通过" key="approved" />
            <TabPane tab="已拒绝" key="rejected" />
            <TabPane tab="全部" key="all" />
          </Tabs>
          
          {(() => {
            const deptId = parseInt(statusFilter.replace('dept-', ''));
            const dept = departments.find(d => d.id === deptId);
            const dataForms = dept ? getDataForms(dept.name) : [];
            
            // Filter submissions by status filter (not using useMemo because this is inside an IIFE)
            let filteredSubmissions = submissions;
            if (deptStatusFilter !== 'all') {
              filteredSubmissions = submissions.filter(s => s.status === deptStatusFilter);
            }
            
            // Group by batch (not using useMemo because this is inside an IIFE)
            const deptBatches = filteredSubmissions.length === 0 
              ? [] 
              : groupSubmissionsByBatch(filteredSubmissions);
            
            // Pagination handling (not using useMemo because this is inside an IIFE)
            const start = (deptBatchPage - 1) * deptBatchPageSize;
            const end = start + deptBatchPageSize;
            const paginatedBatches = deptBatches.slice(start, end);
            
            const getDepartmentName = (deptId) => {
              const dept = departments.find(d => d.id === deptId);
              return dept ? dept.name : `部门ID: ${deptId}`;
            };
            
            // Get pending records for current status (for batch review)
            // Note: useMemo cannot be used inside an IIFE; use normal variable assignment instead
            const currentPendingSubmissions = deptStatusFilter === 'pending' ? filteredSubmissions : [];
            
            if (dataForms && dataForms.length > 0) {
              const isPhdDept = dept && dataForms.some(f => PHD_TYPES.includes(f.key));
              if (isPhdDept) {
                return (
                  <>
                    {filteredSubmissions.length === 0 ? (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                        <p>暂无{deptStatusFilter === 'all' ? '' : deptStatusFilter === 'approved' ? '已通过' : deptStatusFilter === 'rejected' ? '已拒绝' : '待审核'}的记录</p>
                      </div>
                    ) : (
                      <>
                        <PhdReviewMatrix
                          deptName={dept.name}
                          submissions={filteredSubmissions}
                          onCellReview={() => {
                            loadSubmissionsForDepartment(dept.id);
                          }}
                        />
                      </>
                    )}
                  </>
                );
              }
              // When data forms exist, display by batch (non-PhD authorization departments)
              return (
                <>
                  {deptBatches.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      <p>暂无{deptStatusFilter === 'all' ? '' : deptStatusFilter === 'approved' ? '已通过' : deptStatusFilter === 'rejected' ? '已拒绝' : '待审核'}的记录</p>
                    </div>
                  ) : (
                    <>
                      {/* Batch operation dropdown in top-left, only shown for pending status */}
                      {deptStatusFilter === 'pending' && (
                        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                          <Dropdown
                            key="batch-operation-dept"
                            overlay={
                              <Menu
                                onClick={({ key }) => {
                                  if (key === 'approve') {
                                    handleApproveSelected();
                                  } else if (key === 'reject') {
                                    handleRejectSelected();
                                  }
                                }}
                                className="batch-operation-dropdown"
                              >
                                <Menu.Item key="approve" icon={<CheckOutlined />}>
                                  通过选中行
                                </Menu.Item>
                                <Menu.Item key="reject" icon={<CloseOutlined />}>
                                  拒绝选中行
                                </Menu.Item>
                              </Menu>
                            }
                            trigger={['click']}
                            className="batch-operation-dropdown"
                            getPopupContainer={(triggerNode) => triggerNode.parentElement}
                          >
                            <Button 
                              key="batch-operation-button-dept"
                              className="batch-operation-dropdown"
                              loading={reviewing}
                            >
                              批量操作 <DownOutlined />
                            </Button>
                          </Dropdown>
                          <div />
                        </div>
                      )}

                      <Collapse
                        defaultActiveKey={[]}
                        style={{ background: '#fff' }}
                        items={paginatedBatches.map((batch) => {
                        const formConfig = dataForms.find(f => f.key === batch.data_type);
                        return {
                          key: batch.key,
                          label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 'bold' }}>
                                  {getDataTypeLabel(batch.data_type, batch.department_id)}
                                </span>
                                <Tag color="blue">{batch.submissions.length} 条数据</Tag>
                                {batch.status && (
                                  <Tag color={batch.status === 'approved' ? 'green' : batch.status === 'rejected' ? 'red' : 'orange'}>
                                    {batch.status === 'approved' ? '已通过' : batch.status === 'rejected' ? '已拒绝' : '待审核'}
                                  </Tag>
                                )}
                              </div>
                              {deptStatusFilter === 'pending' && (
                                <Space>
                              {/* 已取消“整个批次”审核方式，仅保留按勾选行的批量操作 */}
                                </Space>
                              )}
                            </div>
                          ),
                          children: (
                            <div>
                              <DataTable
                                dataType={formConfig?.key || batch.data_type}
                                submissions={batch.submissions}
                                fields={formConfig?.fields || []}
                                onRefresh={() => {
                                  const deptId = parseInt(statusFilter.replace('dept-', ''));
                                  loadSubmissionsForDepartment(deptId);
                                }}
                                user={currentUser}
                                departmentId={batch.department_id}
                                disableBatchGrouping={true}
                                rowSelection={
                                  deptStatusFilter === 'pending'
                                    ? {
                                        selectedRowKeys: selectedReviewKeys,
                                        onChange: (keys) => {
                                          setSelectedReviewKeys(keys);
                                        },
                                        getCheckboxProps: (record) => ({
                                          disabled: record.status === 'approved' || record.status === 'rejected'
                                        })
                                      }
                                    : undefined
                                }
                              />
                            </div>
                          )
                        };
                      })}
                      />
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          current={deptBatchPage}
                          pageSize={deptBatchPageSize}
                          total={deptBatches.length}
                          onChange={(page, pageSize) => {
                            setDeptBatchPage(page);
                            setDeptBatchPageSize(pageSize);
                          }}
                          onShowSizeChange={(current, size) => {
                            setDeptBatchPage(1);
                            setDeptBatchPageSize(size);
                          }}
                          showSizeChanger
                          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个批次`}
                          pageSizeOptions={['10', '20', '50', '100']}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            } else {
              // When no data forms exist, display directly by batch
              return (
                <>
                  {deptBatches.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      <p>暂无{deptStatusFilter === 'all' ? '' : deptStatusFilter === 'approved' ? '已通过' : deptStatusFilter === 'rejected' ? '已拒绝' : '待审核'}的记录</p>
                    </div>
                  ) : (
                    <>
                      <Collapse
                        defaultActiveKey={[]}
                        style={{ background: '#fff' }}
                        items={paginatedBatches.map(batch => {
                        return {
                          key: batch.key,
                          label: (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 'bold' }}>
                                  {getDepartmentName(batch.department_id)} - {batch.data_type}
                                </span>
                                <Tag color="blue">{batch.submissions.length} 条数据</Tag>
                                {batch.status && (
                                  <Tag color={batch.status === 'approved' ? 'green' : batch.status === 'rejected' ? 'red' : 'orange'}>
                                    {batch.status === 'approved' ? '已通过' : batch.status === 'rejected' ? '已拒绝' : '待审核'}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          ),
                          children: (
                            <div>
                              <Table
                                columns={buildColumns(batch.submissions)}
                                dataSource={batch.submissions}
                                rowKey="id"
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                size="middle"
                                bordered
                              />
                            </div>
                          )
                        };
                      })}
                      />
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                          current={deptBatchPage}
                          pageSize={deptBatchPageSize}
                          total={deptBatches.length}
                          onChange={(page, pageSize) => {
                            setDeptBatchPage(page);
                            setDeptBatchPageSize(pageSize);
                          }}
                          onShowSizeChange={(current, size) => {
                            setDeptBatchPage(1);
                            setDeptBatchPageSize(size);
                          }}
                          showSizeChanger
                          showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 个批次`}
                          pageSizeOptions={['10', '20', '50', '100']}
                        />
                      </div>
                    </>
                  )}
                </>
              );
            }
          })()}
        </>
      ) : statusFilter === 'pending' ? (
        <BatchReviewList
          mode="pending"
          batches={pendingBatches}
          emptyText="暂无待审核的数据"
          departments={departments}
          getDataTypeLabel={getDataTypeLabel}
          buildColumns={buildColumns}
          selectedReviewKeys={selectedReviewKeys}
          onSelectionChange={(keys) => setSelectedReviewKeys(keys)}
          reviewing={reviewing}
          onApproveSelected={handleApproveSelected}
          onRejectSelected={handleRejectSelected}
        />
      ) : statusFilter === 'approved' ? (
        <BatchReviewList
          mode="view"
          batches={approvedBatches}
          emptyText="暂无已通过的记录"
          departments={departments}
          getDataTypeLabel={getDataTypeLabel}
          buildColumns={buildColumns}
          page={batchPage}
          pageSize={batchPageSize}
          onPageChange={(page, pageSize) => {
            setBatchPage(page);
            if (pageSize) setBatchPageSize(pageSize);
          }}
          onPageSizeChange={(size) => {
            setBatchPage(1);
            setBatchPageSize(size);
          }}
        />
      ) : statusFilter === 'rejected' ? (
        <BatchReviewList
          mode="view"
          batches={rejectedBatches}
          emptyText="暂无已拒绝的记录"
          departments={departments}
          getDataTypeLabel={getDataTypeLabel}
          buildColumns={buildColumns}
          page={batchPage}
          pageSize={batchPageSize}
          onPageChange={(page, pageSize) => {
            setBatchPage(page);
            if (pageSize) setBatchPageSize(pageSize);
          }}
          onPageSizeChange={(size) => {
            setBatchPage(1);
            setBatchPageSize(size);
          }}
        />
      ) : statusFilter === 'all' ? (
        <BatchReviewList
          mode="view"
          batches={allBatches}
          emptyText="暂无记录"
          departments={departments}
          getDataTypeLabel={getDataTypeLabel}
          buildColumns={buildColumns}
          page={batchPage}
          pageSize={batchPageSize}
          onPageChange={(page, pageSize) => {
            setBatchPage(page);
            if (pageSize) setBatchPageSize(pageSize);
          }}
          onPageSizeChange={(size) => {
            setBatchPage(1);
            setBatchPageSize(size);
          }}
        />
      ) : statusFilter === 'users' ? (
        <>
          <div style={{ marginBottom: '16px' }}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => {
                setEditingUserId(null);
                userForm.resetFields();
                setUserModalVisible(true);
              }}
            >
              创建用户
            </Button>
          </div>
          <Table
            columns={userColumns}
            dataSource={users}
            rowKey="id"
            loading={usersLoading}
            pagination={{ pageSize: 10 }}
            size="middle"
            bordered
          />
          <Modal
            title={editingUserId ? '编辑用户' : '创建用户'}
            open={userModalVisible}
            onCancel={() => {
              setUserModalVisible(false);
              setEditingUserId(null);
              userForm.resetFields();
            }}
            onOk={() => userForm.submit()}
            okText={editingUserId ? '更新' : '创建'}
            cancelText="取消"
            afterClose={() => {
              setEditingUserId(null);
              userForm.resetFields();
            }}
          >
            <Form
              form={userForm}
              layout="vertical"
              onFinish={editingUserId ? handleUpdateUser : handleCreateUser}
            >
              {!(isSuperAdmin && editingUserId === currentUser?.id) && (
                <Form.Item
                  name="username"
                  label="用户名"
                  rules={[{ required: true, message: '请输入用户名' }]}
                >
                  <Input placeholder="用户名" autoComplete="off" />
                </Form.Item>
              )}
              <Form.Item
                name="password"
                label="密码"
                rules={
                  isSuperAdmin && editingUserId === currentUser?.id
                    ? [{ required: true, message: '请输入新密码' }]
                    : editingUserId
                    ? []
                    : [{ required: true, message: '请输入密码' }]
                }
                help={
                  isSuperAdmin && editingUserId === currentUser?.id
                    ? '请输入新密码'
                    : editingUserId
                    ? '留空则不修改密码'
                    : ''
                }
              >
                <Input.Password 
                  placeholder={
                    isSuperAdmin && editingUserId === currentUser?.id
                      ? '请输入新密码'
                      : editingUserId
                      ? '留空则不修改密码'
                      : '密码'
                  } 
                  autoComplete="new-password" 
                />
              </Form.Item>
              {!(isSuperAdmin && editingUserId === currentUser?.id) && (
                <>
                  <Form.Item
                    name="role"
                    label="角色"
                    initialValue="user"
                  >
                    <Select disabled>
                      <Select.Option value="user">普通用户</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="department_id"
                    label="所属部门"
                    rules={[{ required: true, message: '请选择所属部门' }]}
                  >
                    <Select 
                      placeholder="选择部门"
                      showSearch 
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {departments
                        .filter(dept => dept.name !== '牵头部门' && dept.name !== '博士授权点') // Filter out parent departments
                        .map(dept => (
                          <Select.Option key={dept.id} value={dept.id}>
                            {dept.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </>
              )}
            </Form>
          </Modal>
        </>
          ) : null}

      <RejectModal
        visible={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectTargetIds([]);
          setRejectTargetSubmissions([]);
        }}
        onOk={handleRejectSubmit}
        confirmLoading={rejectSubmitting}
        form={rejectForm}
      >
        {rejectTargetSubmissions.length > 0 && (
          <Form.Item label={`选中的数据行（共 ${rejectTargetSubmissions.length} 条）`}>
            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #E1E5EB', borderRadius: '4px' }}>
              <Table
                columns={buildColumns(rejectTargetSubmissions)}
                dataSource={rejectTargetSubmissions}
                rowKey="id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
                bordered
              />
            </div>
          </Form.Item>
        )}
      </RejectModal>
    </Card>
  );
};

export default AdminPanel;

